import React, { useEffect, useMemo, useRef, useState } from "react";
import { Song } from "../../types";
import useSongStore from "../../store/SongState";
import './styles.scss';
import { areSameSongs, parseTime } from "../../../../../utils/utils";
import classNames from "classnames";
import SquareImage from "../../../../shared/square-image/square-image";


type SongCardViewProps = {
    song: Song;
    onClick?: (song: Song) => void;
    onOptions?: (e: React.MouseEvent, song: Song) => void;
    ableHovering?: boolean;
    extraDetails?: React.ReactNode;
    customOptions?: React.ReactNode;
    indicateQueue?: boolean;
    onScroll?: (song: Song) => void;
    className?: string;
    hidden?: boolean;
    markDownloaded?: boolean;
    height?: number;
}

const SongCardView = ({
    song,
    onClick,
    onOptions,
    ableHovering,
    extraDetails,
    customOptions,
    indicateQueue,
    onScroll,
    className,
    hidden,
    height = 77,
}: SongCardViewProps) => {

    const { state } = useSongStore(state => state);
    const songCardDivRef = React.useRef<HTMLDivElement>(null);
    const startXTouch = useRef(-1);
    const maxScroll = useRef(0);
    const lastXScroll = useRef(0);
    const scrolled = useRef(false);
    const topCardStartPos = useRef(-1);

    const isInQueue = state.songQueue.some(s => areSameSongs(s, song));

    const isSelectedSong = useMemo(() => {
        return areSameSongs(song, state.currentSong);
    }, [song, state.currentSong]);



    const handleOptionsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (onOptions) {
            onOptions(e, song);
        }
    }

    useEffect(() => {
        if (!songCardDivRef.current || !onScroll) {
            return;
        }

        songCardDivRef.current.addEventListener('touchstart', handleScrollStart);
        songCardDivRef.current.addEventListener('touchmove', handleScroll);
        songCardDivRef.current.addEventListener('touchend', handleScrollEnd);
        // if we are scrolling on the body, dont scroll the song card
        songCardDivRef.current.addEventListener('touchmove', (e) => {
            if (e.touches[0].clientX < 50) {
                e.preventDefault();
            }
        });

        return () => {
            songCardDivRef.current?.removeEventListener('touchstart', handleScrollStart);
            songCardDivRef.current?.removeEventListener('touchmove', handleScroll);
            songCardDivRef.current?.removeEventListener('touchend', handleScrollEnd);
        }

    }, [songCardDivRef.current]);

    const handleScrollStart = (e: TouchEvent) => {
        if (!songCardDivRef.current) {
            return;
        }
        maxScroll.current = songCardDivRef.current.getBoundingClientRect().width / 1.2;
        startXTouch.current = e.touches[0].clientX;
        songCardDivRef.current.parentElement!.style.transition = 'none';
        songCardDivRef.current.style.transition = `none`;
        scrolled.current = false;
        topCardStartPos.current = songCardDivRef.current.getBoundingClientRect().top;
    }

    const handleScroll = (e: TouchEvent) => {
        if (!songCardDivRef.current || startXTouch.current === -1) {
            return;
        }
        if (songCardDivRef.current.getBoundingClientRect().top !== topCardStartPos.current) {
            handleScrollEnd();
            return;
        }

        const currentX = e.touches[0].clientX;
        const translateX = Math.max(0, currentX - startXTouch.current);

        songCardDivRef.current.style.translate = `${translateX}px`;
        const percentage = Math.min(1, Math.max(0, (currentX - startXTouch.current - 20) / maxScroll.current));
        const red = Math.max(0, 66 - percentage * 124);
        const green = Math.min(66 + percentage * 124, 190);
        const blue = Math.max(0, 66 - percentage * 124);
        songCardDivRef.current.parentElement!.style.backgroundColor = `rgb(${red},${green},${blue})`;
        scrolled.current = currentX - lastXScroll.current > 0 && currentX > startXTouch.current + maxScroll.current / 3;
        lastXScroll.current = currentX;
    }

    const handleScrollEnd = (e?: TouchEvent) => {
        if (!songCardDivRef.current) {
            return;
        }
        songCardDivRef.current.style.transition = `translate 0.33s ease`;
        songCardDivRef.current.style.translate = `0px`;
        songCardDivRef.current.parentElement!.style.transition = 'background-color 0.33s ease';
        songCardDivRef.current.parentElement!.style.backgroundColor = `var(--bg-card)`;
        if (scrolled.current) {
            onScroll && onScroll(song);
        }
        startXTouch.current = -1;
        scrolled.current = false;
    }

    const handleClickCard = () => {
        if (!onClick) {
            return;
        }
        if (isSelectedSong) {
            return;
        }

        onClick(song);
    }

    if (hidden) {
        return null;
    }

    return (
        <div
            className={classNames("card song-card", className)} id={`song-${song.id}`}
            onContextMenu={handleOptionsClick}
            onClick={handleClickCard}
            style={{ maxWidth: "100%" }}>
            <div className="song-card__scroll-indicator" style={{
                height: height,
                width: height,
            }}>
                <i className="fa-solid fa-plus"></i>
            </div>
            <div
                ref={songCardDivRef}
                className="g-0 d-flex flex-row song-card__card-content"
                style={{
                    backgroundColor: onScroll ? "var(--bg-panel)" : "transparent",
                }}>
                <div style={{ height }}>
                    {ableHovering && <>
                        {!isSelectedSong && <div className="play-song-card" style={{
                            height,
                            width: height,
                        }}>
                            <i className="fa-regular fa-circle-play"></i>
                        </div>}
                        <div className="info-song-image" style={{
                            height,
                            width: height,
                        }}>
                            {isSelectedSong && <div className="playing">
                                <span className="playing__bar playing__bar1"></span>
                                <span className="playing__bar playing__bar2"></span>
                                <span className="playing__bar playing__bar3"></span>
                                <span className="playing__bar playing__bar4"></span>
                                <span className="playing__bar playing__bar5"></span>
                            </div>}
                        </div>
                    </>}
                    <SquareImage
                        song={song}
                        style={{ width: height }}
                        alt="poster"
                        overDetails={
                            <>
                                {indicateQueue && isInQueue &&
                                    <div className="song-card__square-image-over-details-queue">
                                        <i className="fa-solid fa-bars-staggered"></i>
                                    </div>}
                                {song.download_status &&
                                    <div className="song-card__square-image-over-details-cloud">
                                        {song.download_status === 'downloaded' && <i className="fa-regular fa-circle-down"></i>}
                                        {song.download_status === 'downloading' && <i className="fa-solid fa-spinner fa-spin"></i>}
                                        {song.download_status === 'error' && <i className="fa-solid fa-triangle-exclamation"></i>}
                                    </div>}

                                <div className="song-card__square-image-over-details-time">
                                    {parseTime(song.duration)}
                                </div>
                            </>
                        } />
                </div>
                <div style={{
                    maxHeight: height,
                    textAlign: "left",
                    color: "var(--color-white)"
                }} className="card-body pt-0 pe-0 pb-0 ps-2 song-card__info">
                    <div style={{ height: "100%" }}
                        className="song-card__name-info d-flex flex-column justify-content-center">
                        <div style={{
                            fontWeight: "bolder",
                            letterSpacing: "0.5px",
                            fontSize: "1rem",
                            overflow: "hidden",
                            color: "var(--color-white)",
                            textOverflow: "ellipsis",
                            textWrap: "nowrap"
                        }}>{song.name}</div>
                        {!extraDetails && <div style={{
                            fontWeight: "bold",
                            letterSpacing: "0.1px",
                            fontSize: "0.8rem",
                            overflow: "hidden",
                            color: "var(--color-gray-muted)"
                        }}>{song.user.username}
                            {song.is_favorite && <> · <i className="fa-solid fa-heart"></i></>}
                        </div>}
                        {extraDetails}
                    </div>
                    {onOptions && <div className="song-card__options-icon"><i onClick={handleOptionsClick}
                        className="fa-solid fa-ellipsis-vertical p-3"></i>
                    </div>}
                    {customOptions && <div className="song-card__options-icon">{customOptions}</div>}
                </div>
            </div>
        </div>
    );
}

export default SongCardView;
